/**
 * ScribePage — Consultation Scribe with Pre-Consultation Review
 *
 * Single-page flow:
 *   Phase 1: Select patient + consultation type
 *   Phase 2: Pre-consultation review (full letter from referral+form, exam template)
 *   Phase 3: Record consultation (live transcript, collapsible pre-consult)
 *   Phase 4: Generate final letter + detected actions
 *   Phase 5: Review, edit, approve & send
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  Mic, MicOff, Square, Play, Pause, FileText, Check, AlertCircle,
  Clock, User, RefreshCw, Edit3, Save, ChevronDown, ChevronUp,
  Stethoscope, ClipboardList, Send, X, Eye, Loader
} from 'lucide-react';
import API_BASE_URL from '../config/api';

const SAMPLE_RATE = 16000;
const BUFFER_SIZE = 4096;

function ScribePage() {
  // Phase state
  const [phase, setPhase] = useState('select'); // select | preConsult | recording | generate | review

  // Patient
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [noteType, setNoteType] = useState('consultation');

  // Pre-consultation
  const [preConsultLetter, setPreConsultLetter] = useState(null);
  const [preConsultContext, setPreConsultContext] = useState(null);
  const [examTemplate, setExamTemplate] = useState(null);
  const [preConsultLoading, setPreConsultLoading] = useState(false);
  const [preConsultCollapsed, setPreConsultCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('letter'); // letter | exam

  // Session & recording
  const [sessionId, setSessionId] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState('idle'); // idle | recording | paused | stopped
  const [segments, setSegments] = useState([]);
  const [interimText, setInterimText] = useState('');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);

  // Generated output
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [detectedActions, setDetectedActions] = useState([]);
  const [letterId, setLetterId] = useState(null);
  const [editingLetter, setEditingLetter] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [approved, setApproved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [metadata, setMetadata] = useState(null);

  // Refs
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const durationRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // Auto-scroll transcript only within the transcript box (not the whole page)
  useEffect(() => {
    if (transcriptEndRef.current) {
      const container = transcriptEndRef.current.parentElement;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
        if (isNearBottom) {
          transcriptEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }, [segments, interimText]);

  // Duration timer
  useEffect(() => {
    if (recordingStatus === 'recording') {
      durationRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } else {
      if (durationRef.current) clearInterval(durationRef.current);
    }
    return () => { if (durationRef.current) clearInterval(durationRef.current); };
  }, [recordingStatus]);

  // Patient search
  const searchPatients = useCallback(async (query) => {
    if (query.length < 2) { setPatientResults([]); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/patients/search?q=${encodeURIComponent(query)}`);
      setPatientResults(res.data.patients || []);
      setShowDropdown(true);
    } catch (err) { console.error('Patient search error:', err); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPatients(patientSearch), 300);
    return () => clearTimeout(timer);
  }, [patientSearch, searchPatients]);

  // ─── Phase transitions ────────────────────────────────────────────

  const startSession = async () => {
    if (!selectedPatient) return;
    setError(null);
    setPreConsultLoading(true);

    try {
      // Create scribe session
      const sessionRes = await axios.post(`${API_BASE_URL}/scribe/session`, {
        patientId: selectedPatient.id
      });
      setSessionId(sessionRes.data.sessionId);

      // Load pre-consultation letter and exam template in parallel
      const [letterRes, examRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/scribe/pre-consult/${selectedPatient.id}`).catch(() => null),
        selectedPatient.injuryType
          ? axios.get(`${API_BASE_URL}/scribe/exam-template/${selectedPatient.injuryType}`).catch(() => null)
          : Promise.resolve(null)
      ]);

      if (letterRes?.data) {
        setPreConsultLetter(letterRes.data.letter);
        setPreConsultContext(letterRes.data.context);
        // Fetch exam template using injury type from context
        if (letterRes.data.context?.injuryType && !examRes) {
          try {
            const examRes2 = await axios.get(`${API_BASE_URL}/scribe/exam-template/${letterRes.data.context.injuryType}`);
            setExamTemplate(examRes2.data);
          } catch (e) { /* no template */ }
        }
      }
      if (examRes?.data) setExamTemplate(examRes.data);

      setPhase('preConsult');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start session');
    } finally {
      setPreConsultLoading(false);
    }
  };

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: SAMPLE_RATE, channelCount: 1, echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
      processorRef.current = processor;

      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const wsUrl = API_BASE_URL.replace(/^http/, 'ws').replace('/api', '');
      const ws = new WebSocket(`${wsUrl}/api/scribe/ws?token=${token}&sessionId=${sessionId}`);
      ws.binaryType = 'arraybuffer';

      ws.onopen = async () => {
        await axios.post(`${API_BASE_URL}/scribe/session/${sessionId}/start`);
        setRecordingStatus('recording');
        setPhase('recording');
        setDuration(0);
        setPreConsultCollapsed(true);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'transcript') {
            setSegments(msg.segments.filter(s => s.isFinal));
            const interim = msg.segments.find(s => !s.isFinal);
            setInterimText(interim ? interim.text : '');
          }
        } catch (e) { /* binary or unparseable */ }
      };

      ws.onerror = () => setError('Connection error — check your network');
      wsRef.current = ws;

      processor.onaudioprocess = (e) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        ws.send(pcm16.buffer);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else {
        setError(err.message || 'Failed to start recording');
      }
    }
  };

  const pauseRecording = async () => {
    await axios.post(`${API_BASE_URL}/scribe/session/${sessionId}/pause`);
    setRecordingStatus('paused');
  };

  const resumeRecording = async () => {
    await axios.post(`${API_BASE_URL}/scribe/session/${sessionId}/resume`);
    setRecordingStatus('recording');
  };

  const stopRecording = async () => {
    if (processorRef.current) processorRef.current.disconnect();
    if (audioContextRef.current) audioContextRef.current.close();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (wsRef.current) wsRef.current.close();
    await axios.post(`${API_BASE_URL}/scribe/session/${sessionId}/stop`);
    const res = await axios.get(`${API_BASE_URL}/scribe/session/${sessionId}/transcript`);
    setSegments(res.data.segments || []);
    setRecordingStatus('stopped');
  };

  const generateLetter = async () => {
    setGenerating(true);
    setPhase('generate');
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/scribe/session/${sessionId}/generate`, { noteType });
      setGeneratedLetter(res.data.letter);
      setDetectedActions((res.data.actions || []).map(a => ({ ...a, checked: true })));
      setLetterId(res.data.letterId);
      setEditedContent(res.data.letter);
      setMetadata(res.data.metadata);
      setPhase('review');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate letter');
      setPhase('recording');
      setRecordingStatus('stopped');
    } finally {
      setGenerating(false);
    }
  };

  const saveLetter = async () => {
    await axios.put(`${API_BASE_URL}/scribe/note/${letterId}`, { content: editedContent });
    setEditingLetter(false);
  };

  const approveAndSend = async () => {
    await axios.post(`${API_BASE_URL}/scribe/note/${letterId}/approve`);
    setApproved(true);
    // TODO: Enact checked actions (MRI form, referrals, exercise/education sheets)
  };

  const resetSession = () => {
    if (wsRef.current) wsRef.current.close();
    if (processorRef.current) processorRef.current.disconnect();
    if (audioContextRef.current) audioContextRef.current.close();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setPhase('select');
    setSessionId(null);
    setRecordingStatus('idle');
    setSegments([]);
    setInterimText('');
    setDuration(0);
    setPreConsultLetter(null);
    setPreConsultContext(null);
    setExamTemplate(null);
    setPreConsultCollapsed(false);
    setGeneratedLetter(null);
    setDetectedActions([]);
    setLetterId(null);
    setEditingLetter(false);
    setApproved(false);
    setError(null);
  };

  const formatDuration = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  // Speaker colours
  const speakerMap = {};
  let speakerIdx = 0;
  segments.forEach(seg => {
    if (!speakerMap[seg.speaker]) {
      speakerMap[seg.speaker] = { label: speakerIdx === 0 ? 'Doctor' : speakerIdx === 1 ? 'Patient' : `Speaker ${speakerIdx + 1}`, index: speakerIdx };
      speakerIdx++;
    }
  });
  const speakerColors = ['#2563eb', '#16a34a', '#d97706', '#9333ea'];

  // Action type labels
  const actionLabels = {
    mri_referral: 'MRI Referral',
    specialist_referral: 'Specialist Referral',
    physio_referral: 'Physiotherapy Referral',
    exercise_sheet: 'Exercise Sheet',
    education_sheet: 'Education Sheet',
    medication: 'Medication',
    follow_up: 'Follow-up',
    injection: 'Injection',
    other: 'Other'
  };

  const actionIcons = {
    mri_referral: '🔬', specialist_referral: '👨‍⚕️', physio_referral: '🏃',
    exercise_sheet: '💪', education_sheet: '📚', medication: '💊',
    follow_up: '📅', injection: '💉', other: '📋'
  };

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Consultation Scribe</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0' }}>Record, transcribe, and generate clinical documentation</p>
        </div>
        {phase !== 'select' && (
          <button onClick={resetSession} style={{ ...btnStyle('#64748b', true), gap: 6 }}>
            <RefreshCw size={14} /> New Session
          </button>
        )}
      </div>

      {error && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={18} color="#dc2626" />
          <span style={{ color: '#dc2626', flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} color="#dc2626" /></button>
        </div>
      )}

      {/* ─── Phase 1: Patient Selection ─────────────────────────── */}
      {phase === 'select' && (
        <div style={cardStyle}>
          <h2 style={headingStyle}>Select Patient</h2>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <input
              type="text" placeholder="Search by name or NHI..."
              value={patientSearch} onChange={(e) => setPatientSearch(e.target.value)}
              onFocus={() => patientResults.length > 0 && setShowDropdown(true)}
              style={inputStyle}
            />
            {showDropdown && patientResults.length > 0 && (
              <div style={dropdownStyle}>
                {patientResults.map(p => (
                  <div key={p.id} onClick={() => { setSelectedPatient(p); setPatientSearch(p.name); setShowDropdown(false); }}
                    style={dropdownItemStyle}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                    <span style={{ fontWeight: 500 }}>{p.name}</span>
                    <span style={{ color: '#64748b', fontSize: 13 }}>{p.nhi || ''} {p.dateOfBirth ? `| ${p.dateOfBirth}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedPatient && (
            <div style={{ backgroundColor: '#f0f7ff', borderLeft: '4px solid #6b9bd1', padding: 16, borderRadius: '0 8px 8px 0', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{selectedPatient.name}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>
                {[selectedPatient.nhi && `NHI: ${selectedPatient.nhi}`, selectedPatient.dateOfBirth && `DOB: ${selectedPatient.dateOfBirth}`].filter(Boolean).join(' | ')}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <select value={noteType} onChange={e => setNoteType(e.target.value)} style={inputStyle}>
              <option value="consultation">Initial Consultation</option>
              <option value="follow-up">Follow-up</option>
            </select>
            <button onClick={startSession} disabled={!selectedPatient || preConsultLoading}
              style={{ ...btnStyle(selectedPatient ? '#2563eb' : '#94a3b8'), cursor: selectedPatient ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 8 }}>
              {preConsultLoading ? <><Loader size={18} className="spin" /> Loading...</> : 'Start Session'}
            </button>
          </div>
        </div>
      )}

      {/* ─── Phase 2+: Patient Banner ──────────────────────────── */}
      {phase !== 'select' && selectedPatient && (
        <div style={{ ...cardStyle, padding: '12px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <User size={18} color="#6b9bd1" />
            <span style={{ fontWeight: 600 }}>{selectedPatient.name}</span>
            {selectedPatient.nhi && <span style={{ color: '#64748b', fontSize: 13 }}>({selectedPatient.nhi})</span>}
            {preConsultContext?.injuryType && <span style={tagStyle('#e0e7ff', '#4338ca')}>{preConsultContext.injuryType}</span>}
          </div>
          {(recordingStatus === 'recording' || recordingStatus === 'paused') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={16} color="#64748b" />
              <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 600 }}>{formatDuration(duration)}</span>
              {recordingStatus === 'recording' && <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#dc2626', display: 'inline-block', animation: 'pulse 1.5s ease-in-out infinite' }} />}
            </div>
          )}
        </div>
      )}

      {/* ─── Phase 2: Pre-Consultation Review ──────────────────── */}
      {(phase === 'preConsult' || (phase === 'recording' && !preConsultCollapsed)) && preConsultLetter && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setActiveTab('letter')}
                style={{ ...tabStyle, ...(activeTab === 'letter' ? tabActiveStyle : {}) }}>
                <FileText size={14} /> Pre-Consultation Letter
              </button>
              {examTemplate?.sections?.length > 0 && (
                <button onClick={() => setActiveTab('exam')}
                  style={{ ...tabStyle, ...(activeTab === 'exam' ? tabActiveStyle : {}) }}>
                  <Stethoscope size={14} /> Examination Guide
                </button>
              )}
            </div>
            {phase === 'recording' && (
              <button onClick={() => setPreConsultCollapsed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <ChevronUp size={18} />
              </button>
            )}
          </div>

          {activeTab === 'letter' && (
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, maxHeight: 500, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 14 }}>
              {preConsultLetter}
            </div>
          )}

          {activeTab === 'exam' && examTemplate && (
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, maxHeight: 500, overflowY: 'auto' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>{examTemplate.label}</h3>
              {examTemplate.sections.map((section, si) => (
                <div key={si} style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#2563eb', marginBottom: 6 }}>{section.heading}</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {section.items.map((item, ii) => (
                      <span key={ii} style={{ backgroundColor: '#e0e7ff', color: '#3730a3', padding: '3px 10px', borderRadius: 12, fontSize: 12 }}>{item}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Red flags warning */}
          {preConsultContext?.redFlags?.length > 0 && (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginTop: 12 }}>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>Red Flags:</span>
              {preConsultContext.redFlags.map((rf, i) => (
                <span key={i} style={{ marginLeft: 8, color: '#dc2626' }}>{rf.flagType}: {rf.description}</span>
              ))}
            </div>
          )}

          {phase === 'preConsult' && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <button onClick={startRecording} style={btnStyle('#dc2626')}>
                <Mic size={20} /> Start Recording Consultation
              </button>
            </div>
          )}
        </div>
      )}

      {/* Collapsed pre-consult bar */}
      {phase === 'recording' && preConsultCollapsed && preConsultLetter && (
        <div onClick={() => setPreConsultCollapsed(false)}
          style={{ ...cardStyle, padding: '10px 20px', marginBottom: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f0f7ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b9bd1' }}>
            <ClipboardList size={16} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Pre-consultation letter & exam guide — click to expand</span>
          </div>
          <ChevronDown size={16} color="#6b9bd1" />
        </div>
      )}

      {/* ─── Phase 3: Recording + Transcript ───────────────────── */}
      {(phase === 'recording' || (phase === 'preConsult' && recordingStatus !== 'idle')) && (
        <div style={cardStyle}>
          {/* Recording controls */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
            {recordingStatus === 'recording' && (
              <>
                <button onClick={pauseRecording} style={btnStyle('#d97706')}><Pause size={18} /> Pause</button>
                <button onClick={stopRecording} style={btnStyle('#64748b')}><Square size={18} /> Stop</button>
              </>
            )}
            {recordingStatus === 'paused' && (
              <>
                <button onClick={resumeRecording} style={btnStyle('#16a34a')}><Play size={18} /> Resume</button>
                <button onClick={stopRecording} style={btnStyle('#64748b')}><Square size={18} /> Stop</button>
              </>
            )}
            {recordingStatus === 'stopped' && (
              <>
              <button onClick={generateLetter} disabled={segments.length === 0} style={btnStyle('#2563eb')}>
                <FileText size={18} /> Generate Letter & Detect Actions
              </button>
              <button onClick={() => { setSegments([]); setRecordingStatus('idle'); setPhase('preConsult'); setPreConsultCollapsed(false); }} style={btnStyle('#dc2626')}>
                <X size={18} /> Discard Recording
              </button>
              </>
            )}
          </div>

          {/* Transcript */}
          <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, maxHeight: 350, overflowY: 'auto', minHeight: 120 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 10 }}>
              Transcript {segments.length > 0 && `(${segments.length} segments)`}
            </div>
            {segments.length === 0 && (
              <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', marginTop: 30 }}>
                {recordingStatus === 'recording' ? 'Listening...' : 'No transcript segments yet'}
              </p>
            )}
            {segments.map((seg, i) => {
              const sp = speakerMap[seg.speaker] || { label: 'Unknown', index: 0 };
              return (
                <div key={i} style={{ marginBottom: 6, display: 'flex', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: speakerColors[sp.index % 4], minWidth: 65, fontSize: 13, flexShrink: 0 }}>{sp.label}:</span>
                  <span style={{ color: '#1e293b', lineHeight: 1.5 }}>{seg.text}</span>
                </div>
              );
            })}
            {interimText && (
              <div style={{ marginBottom: 6, display: 'flex', gap: 8, opacity: 0.5 }}>
                <span style={{ fontWeight: 600, minWidth: 65, fontSize: 13 }}>...</span>
                <span style={{ fontStyle: 'italic' }}>{interimText}</span>
              </div>
            )}
            <div ref={transcriptEndRef} />
          </div>
        </div>
      )}

      {/* ─── Phase 4: Generating ───────────────────────────────── */}
      {phase === 'generate' && generating && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>
          <Loader size={32} color="#2563eb" className="spin" />
          <p style={{ marginTop: 16, fontSize: 16, color: '#64748b' }}>
            Generating clinical letter and detecting actions from transcript, referral, and patient form...
          </p>
        </div>
      )}

      {/* ─── Phase 5: Review Letter + Actions ──────────────────── */}
      {phase === 'review' && (
        <>
          {/* Letter */}
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={headingStyle}>{approved ? 'Letter Approved' : 'Review Clinical Letter'}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                {!approved && (
                  <>
                    {editingLetter ? (
                      <button onClick={saveLetter} style={btnStyle('#2563eb', true)}><Save size={14} /> Save</button>
                    ) : (
                      <button onClick={() => setEditingLetter(true)} style={btnStyle('#d97706', true)}><Edit3 size={14} /> Edit</button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Context source tags */}
            {metadata?.contextSources && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {metadata.contextSources.hasReferral && <span style={tagStyle('#dbeafe', '#2563eb')}>Referral</span>}
                {metadata.contextSources.hasForm && <span style={tagStyle('#dcfce7', '#16a34a')}>Patient Form</span>}
                <span style={tagStyle('#f3e8ff', '#9333ea')}>{metadata.transcriptSegments} transcript segments</span>
              </div>
            )}

            {editingLetter ? (
              <textarea value={editedContent} onChange={e => setEditedContent(e.target.value)}
                style={{ width: '100%', minHeight: 400, padding: 16, border: '1px solid #d1d5db', borderRadius: 8, fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box' }} />
            ) : (
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 14, maxHeight: 500, overflowY: 'auto' }}>
                {editedContent || generatedLetter}
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
              AI-assisted documentation | ClinHx Scribe | {metadata?.model || 'Claude AI'}
            </div>
          </div>

          {/* Detected Actions */}
          {detectedActions.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <h2 style={headingStyle}>Detected Actions</h2>
              <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
                These actions were detected from your consultation. Uncheck any you don't want to enact.
              </p>
              {detectedActions.map((action, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: i < detectedActions.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <input type="checkbox" checked={action.checked}
                    onChange={() => {
                      const updated = [...detectedActions];
                      updated[i] = { ...updated[i], checked: !updated[i].checked };
                      setDetectedActions(updated);
                    }}
                    style={{ marginTop: 3, width: 18, height: 18, accentColor: '#2563eb' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{actionIcons[action.type] || '📋'}</span>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{actionLabels[action.type] || action.type}</span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
                      {action.description || action.indication || action.reason || action.bodyPart || action.specialty || action.drug || action.timeframe || ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Approve & Send */}
          {!approved ? (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
              <button onClick={approveAndSend} style={btnStyle('#16a34a')}>
                <Check size={18} /> Approve & Send All
              </button>
            </div>
          ) : (
            <div style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Check size={20} color="#16a34a" />
              <span style={{ color: '#16a34a', fontWeight: 600 }}>Letter approved and saved. {detectedActions.filter(a => a.checked).length} action(s) queued.</span>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ─── Style helpers ───────────────────────────────────────────────────

function btnStyle(color, small = false) {
  return {
    backgroundColor: color, color: 'white', border: 'none', borderRadius: 6,
    padding: small ? '8px 14px' : '11px 22px', fontSize: small ? 13 : 14,
    fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8
  };
}

function tagStyle(bg, color) {
  return { backgroundColor: bg, color, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500 };
}

const cardStyle = { backgroundColor: 'white', borderRadius: 8, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 16 };
const headingStyle = { fontSize: 18, fontWeight: 600, margin: '0 0 4px' };
const inputStyle = { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10, maxHeight: 200, overflowY: 'auto' };
const dropdownItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const tabStyle = { background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px 6px 0 0', padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' };
const tabActiveStyle = { backgroundColor: '#f8fafc', borderBottom: '1px solid #f8fafc', color: '#1e293b', fontWeight: 600 };

export default ScribePage;
